import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import getEventStatus from 'utils/eventStatus';
import axios from 'utils/axiosConfig';
import { searchEvents } from '../../redux/actions/events';
import { searchBrands } from '../../redux/actions/brands';
import {
  HeroSection,
  TickerSection,
  FeaturedSection,
  SplitSection,
  ManifestoSection,
  BrandsSection,
  CTASection,
} from './sections';

const ACCENT = '#FFBD59';

const Homepage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { events: rawEvents = [] } = useSelector((state) => state.events || {});
  const { searchResults: brandSearch, brands: brandList = [] } = useSelector(
    (state) => state.brands || {},
  );

  const [counts, setCounts] = useState({ brands: 0, events: 0, bookings: 0 });

  useEffect(() => {
    dispatch(searchEvents({ hidePastEvents: true }));
    dispatch(searchBrands({}, { page: 1, pageSize: 12 }));

    let alive = true;
    axios
      .get('/stats/public')
      .then((res) => {
        if (alive && res?.data) setCounts(res.data);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [dispatch]);

  const events = useMemo(() => {
    return [...rawEvents].sort((a, b) => {
      const aLive = getEventStatus(a?.date) === 'live' ? 1 : 0;
      const bLive = getEventStatus(b?.date) === 'live' ? 1 : 0;
      return bLive - aLive;
    });
  }, [rawEvents]);

  // Section 2 cards: the 4 most recently created (upcoming) events.
  const featured = useMemo(
    () =>
      [...rawEvents]
        .sort(
          (a, b) =>
            new Date(b?.createdAt || 0).getTime() -
            new Date(a?.createdAt || 0).getTime(),
        )
        .slice(0, 4),
    [rawEvents],
  );
  const tickerEvents = useMemo(() => events.slice(0, 10), [events]);

  const brands = useMemo(() => {
    const list = Array.isArray(brandSearch) ? brandSearch : brandSearch?.data || brandList;
    return (list || []).filter((b) => b?.name || b?.brandName);
  }, [brandSearch, brandList]);

  const stats = useMemo(
    () => [
      { value: counts.brands || 0, suffix: '', label: t('homepage.stat_brands_registered') },
      { value: counts.events || 0, suffix: '', label: t('homepage.stat_events_created') },
      { value: counts.bookings || 0, suffix: '', label: t('homepage.stat_bookings') },
    ],
    [counts.brands, counts.events, counts.bookings, t],
  );

  return (
    <div className="home-redesign w-full overflow-x-hidden bg-white text-ink">
      <HeroSection accent={ACCENT} stats={stats} />
      <TickerSection events={tickerEvents} />
      <FeaturedSection events={featured} />
      <SplitSection accent={ACCENT} />
      <ManifestoSection accent={ACCENT} />
      <BrandsSection brands={brands} />
      <CTASection accent={ACCENT} />
    </div>
  );
};

export default Homepage;
